import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BdsSidebar, BdsThemeProvider, BdsGrid } from '@/core/constants/webComponents';
import { MetricsHeader } from '../MetricsHeader/MetricsHeader';
import { MetricsMenu } from '../MetricsMenu/MetricsMenu';
import { getMetricsMenuItems, isMetricsChannel, type MetricsChannel } from '../../metricsMenuItems';
import { getMetricsMenuItemPath, normalizeMetricsChannel, resolveMetricsContextFromPath } from '../../metricsMenuPaths';
import { useMetricsMenuData } from '../../hooks/useMetricsMenuData';
import { MetricsPageSelector, MOCK_PAGES, type PageOption } from '../MetricsPageSelector/MetricsPageSelector';
import type { MenuLocale } from '@/features/menu/constants/menuLabels';

import './MetricsSideBar.scss';

interface MetricsMenuPageProps {
  selectedChannel?: MetricsChannel;
  pageId?: string;
  channelExternalUrl?: string;
  pages?: PageOption[];
  accountName?: string;
  profileImage?: string;
  searchOptions?: Array<any>;
  selectedSearch?: string;
  universeId?: string;
  disabledItemIds?: string[];
  onPageChange?: (page: PageOption) => void;
  onPageSearch?: (searchTerm: string) => void;
}

// Sample pages are only a development fallback and must never reach production.
const DEV_FALLBACK_PAGES: PageOption[] = process.env.NODE_ENV === 'development' ? MOCK_PAGES : [];

const SELECTED_PAGE_STORAGE_KEY = 'metrics-menu:selected-page';

const readStoredPageId = (): string => {
  try {
    return globalThis.sessionStorage?.getItem(SELECTED_PAGE_STORAGE_KEY) ?? '';
  } catch {
    return '';
  }
};

const storePageId = (id: string): void => {
  try {
    globalThis.sessionStorage?.setItem(SELECTED_PAGE_STORAGE_KEY, id);
  } catch {
    // Storage may be unavailable; the selection then only lasts for this page load.
  }
};

const ALL_PLACES_ID = 'allpages';

const comparePages = (a: PageOption, b: PageOption): number =>
  a.channel.localeCompare(b.channel) ||
  Number(b.id === ALL_PLACES_ID) - Number(a.id === ALL_PLACES_ID) || a.title.localeCompare(b.title, undefined, { sensitivity: 'base' });

// Like the legacy menu, "All places" is only offered when there is more than one Google My Business location.
const withAllPlacesPage = (pages: PageOption[], title: string): PageOption[] => {
  const gmbPages = pages.filter((page) => page.channel === 'googlemybusiness' && page.id !== ALL_PLACES_ID);
  if (gmbPages.length < 2 || pages.some((page) => page.id === ALL_PLACES_ID)) return pages;
  return [...pages, { id: ALL_PLACES_ID, title, username: title, profile_pic: '', channel: 'googlemybusiness', acc_id: gmbPages[0].acc_id, externalUrl: '' }];
};

const PAGE_CHANGE_EVENT = 'mfe-metrics-page-change';

export const MetricsSideBar: React.FC<MetricsMenuPageProps> = ({
  selectedChannel,
  pageId: pageIdProp,
  channelExternalUrl = '',
  pages,
  accountName,
  profileImage,
  searchOptions,
  selectedSearch,
  universeId,
  disabledItemIds,
  onPageChange,
  onPageSearch,
}) => {
  const { i18n, t } = useTranslation();
  const [pageSelection, setPageSelection] = useState<{ id: string; source: string } | null>(null);
  const loaded = useMetricsMenuData();
  const pathContext = resolveMetricsContextFromPath(globalThis.location?.pathname ?? '');
  const normalizedChannel = normalizeMetricsChannel(selectedChannel ?? pathContext?.channel ?? '');
  const sourcePages = pages ?? (loaded.pages.length > 0 ? loaded.pages : undefined);
  const pageOptions = withAllPlacesPage(
    (sourcePages ?? (loaded.isLoaded ? DEV_FALLBACK_PAGES : []))
      .map((page) => ({ ...page, channel: normalizeMetricsChannel(page.channel) }))
      .filter((page) => isMetricsChannel(page.channel)),
    t('metricsMenu.allPlaces'),
  ).sort(comparePages);
  const selectionSource = `${selectedChannel ?? ''}:${pageIdProp ?? ''}:${pathContext?.pageId ?? ''}`;
  const candidateIds = [pageSelection?.source === selectionSource ? pageSelection.id : undefined, pathContext?.pageId, pageIdProp, readStoredPageId()];
  const selectedId = candidateIds.find((id) => id && pageOptions.some((page) => page.id === id));
  const selectedPage = pageOptions.find((page) => page.id === selectedId) ?? pageOptions[0];
  const activeChannel = selectedPage?.id === ALL_PLACES_ID ? 'allpages' : (selectedPage?.channel ?? normalizedChannel);
  const activePageId = selectedPage?.id ?? '';
  const activeExternalUrl = selectedPage?.externalUrl ?? channelExternalUrl ?? '';

  const isPathInActiveChannel =
    pathContext?.channel === activeChannel || (activeChannel === 'allpages' && pathContext?.channel === 'googlemybusiness');
  // When the URL belongs to another channel, pre-select the overview of the chosen one.
  const activePath = isPathInActiveChannel
    ? pathContext?.path
    : getMetricsMenuItemPath(activeChannel as MetricsChannel, 'overview') || undefined;

  const handlePageChange = (page: PageOption) => {
    setPageSelection({ id: page.id, source: selectionSource });
    storePageId(page.id);
    onPageChange?.(page);
  };

  const rootRef = useRef<HTMLDivElement>(null);
  const announcedPageRef = useRef<string | null>(null);

  // Persists and announces the resolved page (including the pre-selected first one) once the options are loaded.
  useEffect(() => {
    if (!selectedPage || announcedPageRef.current === selectedPage.id) return;
    announcedPageRef.current = selectedPage.id;
    storePageId(selectedPage.id);
    rootRef.current?.dispatchEvent(
      new CustomEvent(PAGE_CHANGE_EVENT, {
        detail: { pageId: selectedPage.id, channel: selectedPage.channel, title: selectedPage.title },
        bubbles: true,
        composed: true,
      }),
    );
  }, [selectedPage]);

  const metricsItems = useMemo(() => {
    if (!isMetricsChannel(activeChannel)) return [];
    const language = i18n.language.split('-')[0];
    const resolvedLocale: MenuLocale = language === 'en' || language === 'es' ? language : 'pt';
    return getMetricsMenuItems(resolvedLocale, activeChannel, undefined, disabledItemIds);
  }, [i18n.language, activeChannel, disabledItemIds]);
  const channelItem = metricsItems.find((item) => item.id === activeChannel);

  return (
    <div className="menu-sidebar metrics-sidebar" ref={rootRef}>
      <BdsThemeProvider theme="dark">
        <BdsGrid height="100vh" xxs="12">
          <BdsSidebar
            type="fixed"
            is-open={true}
            width={240}
            sidebar-position="left"
            background="surface-4"
            margin={false}
            class="metrics-sidebar-shell"
          >
            <div slot="body" className="metrics-sidebar__layout">
              <div className="metrics-sidebar__header">
                <MetricsHeader
                  accountName={accountName ?? loaded.accountName}
                  profileImage={profileImage}
                  searchOptions={searchOptions}
                  selectedSearch={selectedSearch}
                  universeId={universeId ?? loaded.universeId}
                />
                <div className="metrics-sidebar__page-selector">
                  <MetricsPageSelector
                    key={selectionSource}
                    options={pageOptions}
                    selectedPageId={selectedPage?.id}
                    onChange={handlePageChange}
                    onSearch={onPageSearch}
                  />
                </div>
              </div>

              <div className="metrics-sidebar__body">
                <div className="metrics-sidebar__menu-container">
                  <MetricsMenu
                    key={`${activeChannel}:${activePageId}`}
                    currentPath={activePath}
                    items={channelItem?.children ?? []}
                    channelName={channelItem?.label ?? activeChannel}
                    channel={activeChannel}
                    channelExternalUrl={activeExternalUrl}
                    username={activeChannel === 'googlemybusiness' ? selectedPage?.username : undefined}
                    pageId={activePageId}
                    eventName="mfe-metrics-menu-item-click"
                  />
                </div>
              </div>
            </div>
          </BdsSidebar>
        </BdsGrid>
      </BdsThemeProvider>
    </div>
  );
};